import { Address, hashTypedData, recoverPublicKey, recoverAddress, Hex, WalletClient } from 'viem';

export interface PublicKeyData {
  address: Address;
  publicKey: Hex;
  timestamp: number;
}

const STORAGE_KEY = 'wallet_public_key';

// EIP-712 domain for public key extraction
const domain = {
  name: 'Rankify Public Key Auth',
  version: '1',
  chainId: 1, // Will be updated dynamically
} as const;

const types = {
  PublicKeyAuth: [
    {
      type: 'address',
      name: 'address',
    },
    {
      type: 'uint256',
      name: 'timestamp',
    },
    {
      type: 'string',
      name: 'message',
    },
  ],
} as const;

/**
 * Get cached public key from localStorage
 */
export const getCachedPublicKey = (address: Address): PublicKeyData | null => {
  try {
    const cached = localStorage.getItem(`${STORAGE_KEY}_${address.toLowerCase()}`);
    if (!cached) return null;

    const data: PublicKeyData = JSON.parse(cached);
    return data;
  } catch (error) {
    console.error('Error reading cached public key:', error);
    return null;
  }
};

/**
 * Store public key in localStorage
 */
export const setCachedPublicKey = (data: PublicKeyData): void => {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${data.address.toLowerCase()}`, JSON.stringify(data));
  } catch (error) {
    console.error('Error storing public key:', error);
  }
};

/**
 * Extract public key by signing a message and recovering it
 */
export const extractPublicKey = async (
  walletClient: WalletClient,
  address: Address,
  chainId: number
): Promise<PublicKeyData> => {
  const timestamp = Date.now();
  const message = `Sign this message to authenticate your public key. Timestamp: ${timestamp}`;

  // Create typed data for signing
  const typedData = {
    domain: { ...domain, chainId },
    types,
    primaryType: 'PublicKeyAuth' as const,
    message: {
      address,
      timestamp: BigInt(timestamp),
      message,
    },
  };

  try {
    // Sign the typed data
    const signature = await walletClient.signTypedData({
      account: address,
      ...typedData,
    });

    // Create hash for recovery
    const hash = hashTypedData(typedData);

    // Recover public key and address
    const [publicKey, recoveredAddress] = await Promise.all([
      recoverPublicKey({ hash, signature }),
      recoverAddress({ hash, signature }),
    ]);

    // Verify the signature is from the correct address
    if (recoveredAddress.toLowerCase() !== address.toLowerCase()) {
      throw new Error('Signature verification failed: address mismatch');
    }

    const publicKeyData: PublicKeyData = {
      address,
      publicKey,
      timestamp,
    };

    // Cache the result
    setCachedPublicKey(publicKeyData);

    return publicKeyData;
  } catch (error) {
    console.error('Error extracting public key:', error);
    throw new Error(`Failed to extract public key: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get public key - from cache or by requesting signature
 */
export const getOrExtractPublicKey = async (
  walletClient: WalletClient,
  address: Address,
  chainId: number
): Promise<PublicKeyData> => {
  // Try to get from cache first
  const cached = getCachedPublicKey(address);
  if (cached) {
    console.log('Using cached public key for', address);
    return cached;
  }

  // Extract new public key
  console.log('Extracting new public key for', address);
  return await extractPublicKey(walletClient, address, chainId);
};

/**
 * Clear cached public key
 */
export const clearCachedPublicKey = (address: Address): void => {
  try {
    localStorage.removeItem(`${STORAGE_KEY}_${address.toLowerCase()}`);
  } catch (error) {
    console.error('Error clearing cached public key:', error);
  }
};
